var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { createAlphaFiSDK, validateNetwork } from './networkUtils';
import { getSuiClient } from './client';
import { getTxErrorMessage } from './txResult';
import { getMoveErrorMessage } from './errorMaps';
import { webSocketService } from '../services/websocket';
export const SLUSH_WAL_LOOPING_POOL = '0x0bca47c53d57d203d19611af98a4e723c52cbf1bc58312360bfb5dcba0286de9';
/**
 * Gas budget for claim/cancel of a pending withdrawal, in MIST. 0.05 SUI.
 *
 * Both calls unwind an AlphaLend position, so they are sized against that path's measured
 * cost (21.2M MIST worst case, 9.1M median) — this is ~2.4x the worst case.
 *
 * Known trade-off: unlike the other handlers these two enforce no minimum SUI balance, and
 * a budget must be *reserved* up front even though only actual usage is billed. So a wallet
 * holding less than 0.05 SUI can no longer claim, even though the fee itself is far smaller.
 * Accepted because the alternative — no budget — is the InsufficientGas revert this PR
 * exists to fix. Note the failure is legible but not uniformly worded: a transaction that
 * reaches a validator reports "Insufficient balance for transaction fees", while a wallet
 * that cannot select a gas coin for the reservation fails client-side with a string we do
 * not map.
 */
const GAS_BUDGET_MIST = 50000000;
/**
 * Read the on-chain reason for a reverted transaction and map it to a user-facing message.
 * Without this the locked-loop pool's abort codes never reach the user: a revert lands in
 * `onSuccess` with `success: false` and no error payload, so the effects have to be fetched.
 */
const describeRevert = (network, digest) => __awaiter(void 0, void 0, void 0, function* () {
    // Never throws: this runs inside an async onSuccess whose rejection nothing observes, and
    // there are live throw paths outside the await (client construction on a bad RPC URL,
    // getTxErrorMessage's JSON.stringify on BigInt-bearing effects).
    try {
        const txbCheck = yield getSuiClient(validateNetwork(network))
            .waitForTransaction({ digest, include: { effects: true } })
            .catch(() => null);
        const raw = (txbCheck && getTxErrorMessage(txbCheck)) || '';
        return raw ? getMoveErrorMessage(raw) : 'The transaction reverted on-chain.';
    }
    catch (_a) {
        return 'The transaction reverted on-chain.';
    }
});
/**
 * Handler for claiming Slush pending withdrawals
 * Handles the complete transaction flow including notifications and loading states
 */
export const handleClaimPendingWithdrawals = (params) => __awaiter(void 0, void 0, void 0, function* () {
    const { poolId, withdrawRequestId, address, network, signAndExecuteTransaction, setNotificationInfo, setIsNotificationOpen, setLoadingWithdrawalId, getIcon, displayAmount, } = params;
    if (!address) {
        console.error('No wallet connected');
        return;
    }
    try {
        setLoadingWithdrawalId(withdrawRequestId);
        // Get transaction from SDK
        const alphafiSdk = createAlphaFiSDK(network);
        const transaction = yield alphafiSdk.claimWithdrawSlush({
            withdrawRequestId,
            poolId,
            address,
        });
        setNotificationInfo({
            title: 'Awaiting Response',
            description: 'Awaiting response from the network',
            spinner: true,
        });
        setIsNotificationOpen(true);
        setLoadingWithdrawalId(withdrawRequestId);
        // Execute the transaction
        transaction.setGasBudget(GAS_BUDGET_MIST);
        signAndExecuteTransaction({ transaction }, {
            onSuccess: (result) => __awaiter(void 0, void 0, void 0, function* () {
                // Awaiting inside onSuccess means a throw here would strand the spinner and the
                // row's loading state, and nothing observes this promise's rejection — so the
                // whole body is guarded and the loading state is always cleared.
                try {
                    // `success` is authoritative: onSuccess fires once the tx executed on-chain, so
                    // an on-chain revert arrives HERE rather than in onError. Reporting it as a
                    // successful claim would tell the user their funds moved when they didn't.
                    const link = `https://suiscan.xyz/${network === 'testnet' ? 'testnet' : 'mainnet'}/tx/${result.digest}`;
                    // A revert is where the locked-loop pool's abort codes (still-locked, request
                    // not found, …) actually surface, so fetch the effects and map them rather than
                    // reporting a bare "reverted on-chain".
                    const reason = result.success ? '' : yield describeRevert(network, result.digest);
                    setNotificationInfo(result.success
                        ? {
                            title: 'Withdrawal claim successful',
                            description: displayAmount
                                ? `You have successfully claimed withdrawal of ${displayAmount}.`
                                : 'You have successfully claimed your withdrawal.',
                            image: getIcon('success'),
                            link,
                        }
                        : {
                            title: 'Withdrawal claim failed',
                            description: displayAmount
                                ? `Failed to claim withdrawal of ${displayAmount}. ${reason}`
                                : `Failed to claim your withdrawal. ${reason}`,
                            image: getIcon('failed'),
                            link,
                        });
                    setIsNotificationOpen(true);
                    // Only tell the backend to refresh when the claim actually landed.
                    if (result.success) {
                        webSocketService.sendTransactionNotification(poolId, address);
                    }
                }
                catch (error) {
                    console.error('Failed to report claim outcome:', error);
                }
                finally {
                    setLoadingWithdrawalId(null);
                }
            }),
            onError: (error) => {
                console.error('Claim withdrawal failed:', error);
                setNotificationInfo({
                    title: 'Withdrawal claim failed',
                    description: displayAmount
                        ? `Failed to claim withdrawal of ${displayAmount}. ${getMoveErrorMessage(error.message)}`
                        : getMoveErrorMessage(error.message),
                    image: getIcon('failed'),
                });
                setIsNotificationOpen(true);
                setLoadingWithdrawalId(null);
            },
        });
    }
    catch (error) {
        console.error('Claim withdrawal failed:', error);
        setLoadingWithdrawalId(null);
    }
});
/**
 * Handler for canceling Slush pending withdrawals
 * Handles the complete transaction flow including notifications and loading states
 */
export const handleCancelPendingWithdrawals = (params) => __awaiter(void 0, void 0, void 0, function* () {
    const { poolId, withdrawRequestId, address, network, signAndExecuteTransaction, setNotificationInfo, setIsNotificationOpen, setLoadingWithdrawalId, getIcon, displayAmount, } = params;
    if (!address) {
        console.error('No wallet connected');
        return;
    }
    try {
        setLoadingWithdrawalId(withdrawRequestId);
        // Get transaction from SDK
        const alphafiSdk = createAlphaFiSDK(network);
        const transaction = yield alphafiSdk.cancelWithdrawSlush({
            withdrawRequestId,
            poolId,
            address,
        });
        setNotificationInfo({
            title: 'Awaiting Response',
            description: 'Awaiting response from the network',
            spinner: true,
        });
        setIsNotificationOpen(true);
        setLoadingWithdrawalId(withdrawRequestId);
        // Execute the transaction
        transaction.setGasBudget(GAS_BUDGET_MIST);
        signAndExecuteTransaction({ transaction }, {
            onSuccess: (result) => __awaiter(void 0, void 0, void 0, function* () {
                // Guarded for the same reason as the claim handler above.
                try {
                    // See the claim handler above: an on-chain revert lands in onSuccess, not onError.
                    const link = `https://suiscan.xyz/${network === 'testnet' ? 'testnet' : 'mainnet'}/tx/${result.digest}`;
                    const reason = result.success ? '' : yield describeRevert(network, result.digest);
                    setNotificationInfo(result.success
                        ? {
                            title: 'Withdrawal cancellation successful',
                            description: displayAmount
                                ? `You have successfully cancelled withdrawal of ${displayAmount}.`
                                : 'You have successfully cancelled your withdrawal.',
                            image: getIcon('success'),
                            link,
                        }
                        : {
                            title: 'Withdrawal cancellation failed',
                            description: displayAmount
                                ? `Failed to cancel withdrawal of ${displayAmount}. ${reason}`
                                : `Failed to cancel your withdrawal. ${reason}`,
                            image: getIcon('failed'),
                            link,
                        });
                    setIsNotificationOpen(true);
                    if (result.success) {
                        webSocketService.sendTransactionNotification(poolId, address);
                    }
                }
                catch (error) {
                    console.error('Failed to report cancellation outcome:', error);
                }
                finally {
                    setLoadingWithdrawalId(null);
                }
            }),
            onError: (error) => {
                console.error('Cancel withdrawal failed:', error);
                setNotificationInfo({
                    title: 'Withdrawal cancellation failed',
                    description: displayAmount
                        ? `Failed to cancel withdrawal of ${displayAmount}. ${getMoveErrorMessage(error.message)}`
                        : getMoveErrorMessage(error.message),
                    image: getIcon('failed'),
                });
                setIsNotificationOpen(true);
                setLoadingWithdrawalId(null);
            },
        });
    }
    catch (error) {
        console.error('Cancel withdrawal failed:', error);
        setLoadingWithdrawalId(null);
    }
});
